import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { fetchProducts } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { ReviewsBanner } from "@/components/ReviewsBanner";
import { ArrowRight } from "lucide-react";
import heroStone from "@/assets/hero-stone.png";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "HOWDEN, Certified Lab-Grown Diamonds" },
      {
        name: "description",
        content:
          "Certified lab-grown diamonds, cultivated for brilliance and offered loose by the carat. IGI and GIA graded, traceable, considered.",
      },
      { property: "og:title", content: "HOWDEN, Certified Lab-Grown Diamonds" },
      {
        property: "og:description",
        content:
          "Certified lab-grown diamonds, cultivated for brilliance and offered loose by the carat. IGI and GIA graded, traceable, considered.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/" },
    ],
    links: [
      { rel: "canonical", href: "https://howdendiamonds.com/" },
      { rel: "preload", as: "image", href: heroStone, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://howdendiamonds.com/#organization",
              name: "HOWDEN",
              url: "https://howdendiamonds.com/",
              description:
                "HOWDEN offers certified lab-grown diamonds, cultivated for brilliance and sold loose by the carat.",
            },
            {
              "@type": "WebSite",
              "@id": "https://howdendiamonds.com/#website",
              name: "HOWDEN",
              url: "https://howdendiamonds.com/",
              publisher: { "@id": "https://howdendiamonds.com/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const { data: products = [] } = useQuery({
    queryKey: ["products", "featured"],
    queryFn: () => fetchProducts(6),
  });

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 pb-24 pt-20 lg:grid-cols-12 lg:gap-24 lg:px-12 lg:pb-32 lg:pt-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 lg:pt-12"
          >
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              Lab-Grown · Certified
            </p>
            <h1 className="mt-8 font-serif text-5xl font-light leading-[1.05] sm:text-6xl lg:text-7xl xl:text-8xl">
              Light,
              <br />
              <em className="font-light italic text-accent-foreground/80">
                cultivated
              </em>{" "}
              by
              <br />
              the carat.
            </h1>
            <p className="mt-10 max-w-md text-base leading-relaxed text-muted-foreground">
              HOWDEN cultivates lab-grown diamonds of exceptional clarity and
              fire. Each stone independently graded, offered loose, ready
              for the setting of your choosing.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-8">
              <Link
                to="/shop"
                className="group flex h-12 items-center gap-3 bg-foreground px-8 text-xs uppercase tracking-luxe text-background transition-opacity hover:opacity-90"
              >
                View the Diamonds
                <ArrowRight
                  className="h-3 w-3 transition-transform group-hover:translate-x-1"
                  strokeWidth={1.25}
                />
              </Link>
              <Link
                to="/about"
                className="border-b border-foreground/30 pb-1 text-xs uppercase tracking-luxe transition-colors hover:border-foreground"
              >
                Our Philosophy
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="relative lg:col-span-5 lg:-ml-12 xl:-ml-20"
          >
            <figure className="relative aspect-[3/4]">
              <img
                src={heroStone}
                alt="A 2 carat round-brilliant lab-grown diamond, IGI certified"
                width={1024}
                height={1536}
                loading="eager"
                fetchPriority="high"
                className="absolute inset-0 h-full w-full object-contain"
              />
              <figcaption className="absolute inset-x-0 bottom-0 text-center text-[10px] uppercase tracking-luxe text-muted-foreground/70">
                2.00 ct · Round Brilliant · Excellent
              </figcaption>
            </figure>
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-y border-hairline bg-card">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-3 lg:px-12">
          {[
            {
              title: "Cultivated, Not Mined",
              body:
                "Each diamond is grown in a controlled environment, chemically and optically identical to mined, with origins fully traceable.",
            },
            {
              title: "Independently Certified",
              body:
                "Every stone above 0.30 ct ships with an IGI or GIA grading report covering the 4Cs and full optical proportions.",
            },
            {
              title: "Loose, by the Carat",
              body:
                "Solitaires and melee, sold loose and ready to be set by the jeweller of your choosing. No house mark-up on settings.",
            },
          ].map((p) => (
            <div key={p.title}>
              <h2 className="font-serif text-2xl font-light">{p.title}</h2>
              <div className="my-5 h-px w-10 bg-accent" />
              <p className="text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Live reviews banner */}
      <ReviewsBanner />

      {/* Featured collection */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32">
        <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              Just Cut
            </p>
            <h2 className="mt-4 font-serif text-4xl font-light lg:text-5xl">
              The Diamonds
            </h2>
          </div>
          <Link
            to="/shop"
            className="group flex items-center gap-2 text-xs uppercase tracking-luxe"
          >
            View all
            <ArrowRight
              className="h-3 w-3 transition-transform group-hover:translate-x-1"
              strokeWidth={1.25}
            />
          </Link>
        </div>

        {products.length === 0 ? (
          <EmptyCollection />
        ) : (
          <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <ProductCard key={p.node.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* Editorial */}
      <section className="border-t border-hairline">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
          <div>
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              The House
            </p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight lg:text-5xl">
              Cultivated stones. Considered selection.
            </h2>
          </div>
          <div className="flex items-end">
            <div>
              <p className="text-base leading-relaxed text-muted-foreground">
                We curate a small inventory of certified lab-grown diamonds,
                each one hand-selected for cut, brilliance and proportion.
                No two stones in the vault grade the same — and we hold
                each to a standard we would choose for ourselves.
              </p>
              <Link
                to="/about"
                aria-label="Read more about Howden's diamond sourcing and grading standards"
                className="mt-8 inline-block border-b border-foreground pb-1 text-xs uppercase tracking-luxe"
              >
                Our sourcing standards
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function EmptyCollection() {
  return (
    <div className="flex flex-col items-center justify-center border border-dashed border-hairline px-6 py-24 text-center">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        In the vault
      </p>
      <h3 className="mt-4 max-w-md font-serif text-3xl font-light">
        New stones are being graded.
      </h3>
    </div>
  );
}
